/**
 * AppPostureTab — formerly AppPermissionsTab. Wraps the OAuth-app inventory
 * + filters + table + detail drawer in the editorial shell (PillarBrief +
 * SectionEyebrow + section breaks). All hard-preserves carry over: sync
 * mutation, acknowledgment workflow, severity / verified / status filters,
 * pagination, detail drawer.
 *
 * Phase A scope: composer-driven standfirst, editorial palette tokens, the
 * existing summary cards / filters / table preserved underneath. Phase B
 * (top-three triptych) and Phase C (permission heatmap, recent-consents
 * feed) land in follow-up PRs once their backend support ships.
 */

import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react'
import {
  useQuery,
  useQueries,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { useMsal } from '@azure/msal-react'
import {
  ArrowPathIcon,
  InformationCircleIcon,
  ShieldExclamationIcon,
  ShieldCheckIcon as ShieldCheckSolid,
  XMarkIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

import { api } from '@/lib/api'
import PillarBrief from '@/components/PostureBrief/PillarBrief'
import { SectionEyebrow, SectionHeadline } from '@/components/PostureBrief/SectionHeadline'
import TierStackedBar from '@/components/PostureBrief/TierStackedBar'
import EditorialHeatmap, {
  type HeatmapRow,
  type Intensity,
} from '@/components/PostureBrief/EditorialHeatmap'
import {
  composeAppPostureBrief,
  whyItMatters,
} from '@/lib/security-center/app-posture-brief'

import { AppPermissionsSummaryCards } from './AppPermissionsSummaryCards'
import { AppPermissionsFilters } from './AppPermissionsFilters'
import { AppPermissionsTable } from './AppPermissionsTable'
import { AppPermissionsDetailDrawer } from './AppPermissionsDetailDrawer'
import ConsentFeed from './ConsentFeed'
import type {
  AcknowledgeOAuthAppRequest,
  AcknowledgmentFilterStatus,
  ConsentRecordRow,
  OAuthApp,
  OAuthAppDetail,
  OAuthAppPermission,
  OAuthAppSummary,
  OAuthAppsPagedResponse,
  PermissionHeatmap,
} from './appPermissionsTypes'

function formatSyncTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago at ${time}`
  if (diffDays === 1) return `yesterday at ${time}`
  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} at ${time}`
}

export interface AppPostureTabProps {
  tenantId: string | undefined
}

export function AppPostureTab({ tenantId }: AppPostureTabProps) {
  const queryClient = useQueryClient()
  const { accounts } = useMsal()
  const userEmail = accounts[0]?.username

  // Filter state — preserved verbatim from AppPermissionsTab.
  const [riskTier, setRiskTier] = useState<string | undefined>(undefined)
  const [appType, setAppType] = useState<string | undefined>('NonMicrosoft')
  const [status, setStatus] = useState<string | undefined>(undefined)
  const [verified, setVerified] = useState<string | undefined>(undefined)
  const [acknowledgmentStatus, setAcknowledgmentStatus] =
    useState<AcknowledgmentFilterStatus | undefined>(undefined)
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 20

  // Detail drawer state.
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  // Reset page when filters or tenant change.
  useEffect(() => {
    setCurrentPage(1)
  }, [tenantId, riskTier, appType, status, verified, acknowledgmentStatus, search])

  /** Acknowledgment workflow — HARD PRESERVE. */
  const handleAcknowledge = useCallback(
    async (appId: string, request: AcknowledgeOAuthAppRequest) => {
      if (!tenantId) return
      try {
        await api.acknowledgeOAuthApp(tenantId, appId, request)
        toast.success('App acknowledgment saved')
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['oauth-apps-summary'] }),
          queryClient.invalidateQueries({ queryKey: ['oauth-apps'] }),
          queryClient.invalidateQueries({ queryKey: ['oauth-app-detail', tenantId, selectedAppId] }),
        ])
      } catch {
        toast.error('Failed to save acknowledgment')
        throw new Error('Acknowledgment failed')
      }
    },
    [tenantId, selectedAppId, queryClient],
  )

  // Summary query — drives PillarBrief and SummaryCards.
  const { data: summary, isLoading: summaryLoading } = useQuery<OAuthAppSummary>({
    queryKey: ['oauth-apps-summary', tenantId],
    queryFn: () => api.getOAuthAppSummary(tenantId!),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchInterval: (query) =>
      (query.state.data as OAuthAppSummary | undefined)?.syncInProgress ? 10_000 : false,
  })

  // Apps list query.
  const { data: appsResponse, isLoading: appsLoading } = useQuery<OAuthAppsPagedResponse>({
    queryKey: ['oauth-apps', tenantId, riskTier, appType, status, verified, search, currentPage],
    queryFn: () =>
      api.getOAuthApps(tenantId!, currentPage, pageSize, riskTier, appType, status, verified, search),
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  // App detail query (fires when a row is clicked).
  const { data: appDetail, isLoading: detailLoading } = useQuery<OAuthAppDetail>({
    queryKey: ['oauth-app-detail', tenantId, selectedAppId],
    queryFn: () => api.getOAuthAppDetail(tenantId!, selectedAppId!),
    enabled: !!tenantId && !!selectedAppId && isDrawerOpen,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  // Unfiltered apps query — drives the top-three triptych below. The
  // filter-aware query above varies with the user's filter state; the
  // triptych must rank against the full estate, not the filtered slice.
  const { data: triageAppsResponse } = useQuery<OAuthAppsPagedResponse>({
    queryKey: ['oauth-apps', tenantId, 'triage-top'],
    queryFn: () =>
      api.getOAuthApps(
        tenantId!,
        1,
        50,
        undefined,
        'NonMicrosoft',
        undefined,
        undefined,
        undefined,
      ),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  // Sync mutation — HARD PRESERVE.
  const syncMutation = useMutation({
    mutationFn: () => api.syncOAuthApps(tenantId!),
    onSuccess: async () => {
      toast.success('OAuth app sync started. Data will refresh shortly.')
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['oauth-apps-summary'] }),
        queryClient.invalidateQueries({ queryKey: ['oauth-apps'] }),
      ])
    },
    onError: () => toast.error('Failed to sync OAuth apps'),
  })

  const handleAppClick = (app: OAuthApp) => {
    setSelectedAppId(app.id)
    setIsDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false)
    setTimeout(() => setSelectedAppId(null), 300)
  }

  const handleClearAllFilters = () => {
    setRiskTier(undefined)
    setAppType(undefined)
    setStatus(undefined)
    setVerified(undefined)
    setAcknowledgmentStatus(undefined)
    setSearch('')
  }

  /* --------------------------------------------------------------------- */
  /* Top-three triptych selection (Phase B §8.2)                           */
  /* --------------------------------------------------------------------- */

  /**
   * Pick the three apps that best deserve a closer look. Sort by tier
   * (Critical=0, High=1, Medium=2, Low=3) ascending; tie-break on
   * `aiRiskScore` desc then `highPrivilegePermissionCount` desc. Filter out
   * apps the operator has already cleared with status='Approved' so the
   * triptych surfaces real concerns rather than previously-acknowledged ones.
   */
  const topThreeApps = useMemo<OAuthApp[]>(() => {
    const all = triageAppsResponse?.items ?? []
    const tierRank: Record<OAuthApp['overallRiskTier'], number> = {
      Critical: 0,
      High: 1,
      Medium: 2,
      Low: 3,
    }
    const candidates = all.filter(
      (a) => (a.acknowledgmentStatus ?? '').toLowerCase() !== 'approved',
    )
    const sorted = [...candidates].sort((a, b) => {
      const tierDiff =
        (tierRank[a.overallRiskTier] ?? 99) - (tierRank[b.overallRiskTier] ?? 99)
      if (tierDiff !== 0) return tierDiff
      const aiDiff = (b.aiRiskScore ?? -1) - (a.aiRiskScore ?? -1)
      if (aiDiff !== 0) return aiDiff
      return (
        (b.highPrivilegePermissionCount ?? 0) -
        (a.highPrivilegePermissionCount ?? 0)
      )
    })
    return sorted.slice(0, 3)
  }, [triageAppsResponse])

  /* --------------------------------------------------------------------- */
  /* Phase B detail-fetch (top-three named scopes)                         */
  /* --------------------------------------------------------------------- */

  const triptychDetailQueries = useQueries({
    queries: topThreeApps.map((app) => ({
      queryKey: ['oauth-app-detail', tenantId, app.id],
      queryFn: () => api.getOAuthAppDetail(tenantId!, app.id),
      enabled: !!tenantId && topThreeApps.length > 0,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    })),
  })

  /* --------------------------------------------------------------------- */
  /* Phase C — heatmap + recent consents                                   */
  /* --------------------------------------------------------------------- */

  const hasNoData =
    !summaryLoading && (!summary || (summary?.totalApps ?? 0) === 0)

  const { data: heatmap, isLoading: heatmapLoading } = useQuery<PermissionHeatmap>({
    queryKey: ['oauth-permission-heatmap', tenantId],
    queryFn: () => api.getOAuthPermissionHeatmap(tenantId!),
    enabled: !!tenantId && !hasNoData,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const { data: recentConsents, isLoading: consentsLoading } = useQuery<
    ConsentRecordRow[]
  >({
    queryKey: ['oauth-recent-consents', tenantId],
    queryFn: () => api.getOAuthRecentConsents(tenantId!, 10),
    enabled: !!tenantId && !hasNoData,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  /* --------------------------------------------------------------------- */
  /* Composer-driven brief                                                 */
  /* --------------------------------------------------------------------- */

  /**
   * Estimate how many of the top-tier (Critical+High) apps come from
   * unverified, non-first-party publishers. Used to pick the
   * "two unverified ones overreach" verdict variant.
   */
  const unverifiedTier1Count = useMemo(() => {
    const all = triageAppsResponse?.items ?? []
    return all.filter(
      (a) =>
        (a.overallRiskTier === 'Critical' || a.overallRiskTier === 'High') &&
        !a.isFirstParty &&
        a.isVerifiedPublisher === false,
    ).length
  }, [triageAppsResponse])

  const topConcernAppForBrief = useMemo(() => {
    const top = topThreeApps[0]
    if (!top) return undefined
    return {
      displayName: top.displayName,
      isVerifiedPublisher: top.isVerifiedPublisher,
      tier: top.overallRiskTier,
    }
  }, [topThreeApps])

  const brief = useMemo(
    () =>
      composeAppPostureBrief({
        userEmail,
        tenantId,
        summary: summary ?? null,
        topConcernApp: topConcernAppForBrief,
        unverifiedTier1Count,
        newThisWeek: summary?.newAppsLast7Days ?? null,
      }),
    [summary, tenantId, userEmail, topConcernAppForBrief, unverifiedTier1Count],
  )

  const total = summary?.totalApps ?? 0
  const tierComponents = summary
    ? [
        { label: 'Critical' as const, count: summary.criticalCount },
        { label: 'High' as const, count: summary.highCount },
        { label: 'Medium' as const, count: summary.mediumCount },
        { label: 'Low' as const, count: summary.lowCount },
      ]
    : []

  // Empty state — tenant not selected.
  if (!tenantId) {
    return (
      <NoTenantState />
    )
  }

  // Empty state — sync hasn't run yet.
  if (hasNoData) {
    return (
      <NoSyncState
        onSync={() => syncMutation.mutate()}
        isSyncing={syncMutation.isPending}
      />
    )
  }

  return (
    <div data-tone="bpa">
      <PillarBrief
        brief={brief}
        score={total}
        components={[]}
        tone="bpa"
        isLoading={summaryLoading}
        aside={
          summary ? (
            <InventoryAside total={total} tiers={tierComponents} />
          ) : null
        }
        footerNote="Findings on this page roll up into **Best Practice**. App Posture is inventory, not a separate Guardian score."
      />

      <SyncBadge
        summary={summary}
        isSyncing={syncMutation.isPending}
        onSync={() => syncMutation.mutate()}
      />

      {/* Phase B §8.2 — Three apps worth a closer look. */}
      <TopThreeTriptych
        apps={topThreeApps}
        details={triptychDetailQueries.map((q) => q.data)}
        onAppClick={handleAppClick}
      />

      {/* Phase C §8.3 + §8.4 — Permission heatmap + publisher trust donut. */}
      <PermissionHeatmapSection
        heatmap={heatmap}
        isLoading={heatmapLoading}
        summary={summary}
      />

      {/* Phase C §8.5 — Recent consents feed. */}
      <RecentConsentsSection
        rows={recentConsents}
        isLoading={consentsLoading}
      />

      <OAuthEducationSection />

      <section className="mt-10">
        <SectionEyebrow text="Section · 04 · App inventory" />
        <SectionHeadline level="page">
          App inventory <em>at a glance.</em>
        </SectionHeadline>
        <p className="mt-2 max-w-2xl text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
          Filters narrow the list. Click any row for the full permission grant detail.
        </p>

        <div className="mt-6">
          <AppPermissionsSummaryCards
            summary={summary}
            isLoading={summaryLoading}
            onFilterClick={setRiskTier}
            activeFilter={riskTier}
          />
        </div>

        {summary && (
          <div className="mt-6 max-w-xs">
            <AdminConsentKpi summary={summary} />
          </div>
        )}

        {summary && summary.microsoftAppCount > 0 && appType !== 'Microsoft' && (
          <div className="mt-4 flex items-center gap-2 text-xs italic text-[color:var(--color-faint,#9CA3AF)]">
            <InformationCircleIcon className="h-3.5 w-3.5 flex-shrink-0" />
            <span>{summary.microsoftAppCount.toLocaleString()} Microsoft first-party apps hidden.</span>
            <button
              onClick={() => setAppType('Microsoft')}
              className="text-[color:var(--color-secondaryBlue-300,#4F86A8)] underline underline-offset-2 hover:opacity-80"
            >
              Show
            </button>
          </div>
        )}

        <div className="mt-6">
          <AppPermissionsFilters
            riskTier={riskTier}
            appType={appType}
            status={status}
            verified={verified}
            acknowledgmentStatus={acknowledgmentStatus}
            search={search}
            onRiskTierChange={setRiskTier}
            onAppTypeChange={setAppType}
            onStatusChange={setStatus}
            onVerifiedChange={setVerified}
            onAcknowledgmentStatusChange={setAcknowledgmentStatus}
            onSearchChange={setSearch}
            onClearAll={handleClearAllFilters}
          />
        </div>

        <div className="mt-6">
          <AppPermissionsTable
            apps={appsResponse?.items ?? []}
            total={appsResponse?.total ?? 0}
            pageNumber={appsResponse?.pageNumber ?? currentPage}
            pageSize={appsResponse?.pageSize ?? pageSize}
            totalPages={appsResponse?.totalPages ?? 0}
            isLoading={appsLoading}
            onPageChange={setCurrentPage}
            onAppClick={handleAppClick}
          />
        </div>
      </section>

      <AppPermissionsDetailDrawer
        app={appDetail ?? null}
        isOpen={isDrawerOpen}
        isLoading={detailLoading}
        onClose={handleCloseDrawer}
        onAcknowledge={handleAcknowledge}
      />
    </div>
  )
}

export default AppPostureTab

/* -------------------------------------------------------------------------- */
/* Sub-components                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Right-rail aside for the App Posture brief. Replaces the default ScoreBox
 * because App Posture is *inventory*, not a Guardian-score pillar — showing
 * a 0-100 score-box visual would falsely signal that this page contributes
 * to the headline composite. Surfaces total count + tier breakdown instead.
 */
function InventoryAside({
  total,
  tiers,
}: {
  total: number
  tiers: { label: 'Critical' | 'High' | 'Medium' | 'Low'; count: number }[]
}) {
  return (
    <aside
      aria-label="OAuth app inventory snapshot"
      className="rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[#212938] p-6 text-white"
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/60">
        Apps in tenant
      </div>
      <div className="font-display mt-2 text-[56px] font-extrabold leading-none tabular-nums">
        {total.toLocaleString()}
      </div>

      <div className="mt-6 text-[10px] font-bold uppercase tracking-[0.18em] text-white/60">
        Tier breakdown
      </div>
      <div className="mt-3">
        <TierStackedBar tiers={tiers} total={total} />
      </div>
    </aside>
  )
}

function SyncBadge({
  summary,
  isSyncing,
  onSync,
}: {
  summary: OAuthAppSummary | undefined
  isSyncing: boolean
  onSync: () => void
}) {
  return (
    <div className="mt-6 flex items-center justify-between gap-4">
      {summary ? (
        <div className="inline-flex items-center gap-2 rounded-full border border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)] px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] text-[color:var(--color-mute,#6B7280)]">
          {summary.syncInProgress || isSyncing ? (
            <>
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" />
              <span>Syncing…</span>
            </>
          ) : (
            <>
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              <span>Last synced: {summary.lastSyncAt ? formatSyncTime(summary.lastSyncAt) : 'never'}</span>
            </>
          )}
        </div>
      ) : (
        <div />
      )}

      <button
        onClick={onSync}
        disabled={isSyncing}
        className="inline-flex items-center gap-2 rounded-full bg-[#212938] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#0F1722] disabled:cursor-not-allowed disabled:opacity-60"
      >
        <ArrowPathIcon className={`h-3.5 w-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
        {isSyncing ? 'Syncing…' : 'Sync apps'}
      </button>
    </div>
  )
}

function NoTenantState() {
  return (
    <div className="rounded-[4px] border border-dashed border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] px-12 py-12 text-center">
      <ShieldExclamationIcon className="mx-auto h-12 w-12 text-[color:var(--color-faint,#9CA3AF)]" />
      <h3 className="mt-4 text-lg font-semibold text-[color:var(--color-navy,#212938)]">Select a tenant</h3>
      <p className="mt-1 text-sm italic text-[color:var(--color-mute,#6B7280)]">
        Choose a tenant from the selector above to view OAuth application posture.
      </p>
    </div>
  )
}

function NoSyncState({ onSync, isSyncing }: { onSync: () => void; isSyncing: boolean }) {
  return (
    <div className="rounded-[4px] border border-dashed border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] px-12 py-12 text-center">
      <ShieldExclamationIcon className="mx-auto h-12 w-12 text-[color:var(--color-faint,#9CA3AF)]" />
      <h3 className="mt-4 text-lg font-semibold text-[color:var(--color-navy,#212938)]">No OAuth apps discovered yet</h3>
      <p className="mt-1 text-sm italic text-[color:var(--color-mute,#6B7280)]">
        Sync your tenant to discover OAuth applications and their permission risk levels.
      </p>
      <button
        onClick={onSync}
        disabled={isSyncing}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#212938] px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#0F1722] disabled:cursor-not-allowed disabled:opacity-60"
      >
        <ArrowPathIcon className={`h-3.5 w-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
        {isSyncing ? 'Syncing…' : 'Sync apps now'}
      </button>
    </div>
  )
}

const EDUCATION_DISMISSED_KEY = 'guardian-oauth-education-dismissed'

/** Always-available educational section — opens up by default, dismissible. */
function OAuthEducationSection() {
  const [isDismissed, setIsDismissed] = useState(() => {
    try {
      return localStorage.getItem(EDUCATION_DISMISSED_KEY) === 'true'
    } catch {
      return false
    }
  })

  const handleDismiss = () => {
    setIsDismissed(true)
    try {
      localStorage.setItem(EDUCATION_DISMISSED_KEY, 'true')
    } catch {
      /* noop */
    }
  }

  const handleShow = () => {
    setIsDismissed(false)
    try {
      localStorage.removeItem(EDUCATION_DISMISSED_KEY)
    } catch {
      /* noop */
    }
  }

  if (isDismissed) {
    return (
      <button
        onClick={handleShow}
        className="mt-6 inline-flex items-center gap-1.5 text-xs italic text-[color:var(--color-secondaryBlue-300,#4F86A8)] hover:opacity-80"
      >
        <InformationCircleIcon className="h-4 w-4" />
        Show OAuth posture guide
      </button>
    )
  }

  return (
    <section className="mt-10 rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-cream,#FBF8F2)] p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <ShieldCheckSolid className="h-5 w-5 flex-shrink-0 text-[color:var(--color-secondaryBlue-300,#4F86A8)]" />
          <h3 className="text-sm font-semibold text-[color:var(--color-navy,#212938)]">
            Understanding OAuth app posture
          </h3>
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 rounded-md p-1 text-[color:var(--color-mute,#6B7280)] transition-colors hover:bg-[color:var(--color-paper,#fff)] hover:text-[color:var(--color-navy,#212938)]"
          aria-label="Dismiss guide"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>

      <p className="mt-3 text-sm leading-relaxed text-[color:var(--color-navy,#212938)]">
        Enterprise applications in your Microsoft 365 tenant hold permissions to read, modify, or
        delete organizational data. Posture here means three things: how broad each app's scopes
        are, whether the publisher is verified, and whether the app is still actively used.
      </p>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <h4 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)]">
            Why this matters
          </h4>
          <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-[color:var(--color-navy,#212938)]/80">
            <li>Over-privileged apps widen the blast radius if compromised.</li>
            <li>Unverified third-party apps may not follow security best practices.</li>
            <li>Stale apps with active credentials are a quiet attack vector.</li>
            <li>Apps with org-wide access affect every user, not just consenters.</li>
          </ul>
        </div>
        <div>
          <h4 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)]">
            What to look for
          </h4>
          <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-[color:var(--color-navy,#212938)]/80">
            <li>Critical or High tier — review scopes and ensure least-privilege.</li>
            <li>Apps with directory roles — these hold implicit admin access.</li>
            <li>Unverified publishers — verify the vendor's identity and posture.</li>
            <li>Orphan apps (no owners) — assign one for accountability.</li>
            <li>Stale apps — disable or remove if they haven't signed in recently.</li>
            <li>Secret-based credentials — prefer certificate or federated identity.</li>
          </ul>
        </div>
      </div>

      <p className="mt-4 text-xs italic text-[color:var(--color-mute,#6B7280)]">
        Use Approve to record a review decision and keep an audit trail.
      </p>
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* Editorial KPI tile                                                         */
/* -------------------------------------------------------------------------- */

function KpiCard({
  label,
  value,
  hint,
  onClick,
  accent,
}: {
  label: string
  value: number
  hint?: string
  onClick?: () => void
  accent?: string
}) {
  const interactive = onClick != null
  return (
    <div
      className={[
        'rounded-[4px] border bg-[color:var(--color-paper,#fff)] p-5',
        'border-[color:var(--color-line,#E6E8EC)]',
        interactive
          ? 'cursor-pointer transition-shadow hover:shadow-md'
          : '',
      ].join(' ')}
      onClick={interactive ? onClick : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick?.()
              }
            }
          : undefined
      }
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)]">
        {label}
      </div>
      <div
        className="font-display mt-2 text-[40px] font-extrabold leading-none tabular-nums"
        style={{ color: accent ?? 'var(--color-navy,#212938)' }}
      >
        {value.toLocaleString()}
      </div>
      {hint && (
        <div className="mt-2 text-[11px] italic text-[color:var(--color-faint,#9CA3AF)]">
          {hint}
        </div>
      )}
    </div>
  )
}

/**
 * Admin-consent KPI tile rendered inside the App inventory section. Prefers
 * the real `adminConsentedCount` summary field; when the backend hasn't
 * backfilled it yet, falls back to `staleCount` with a relabel so we surface a
 * useful adjacent signal rather than a blank tile.
 */
function AdminConsentKpi({ summary }: { summary: OAuthAppSummary }) {
  const hasAdmin = typeof summary.adminConsentedCount === 'number'
  const value = hasAdmin ? summary.adminConsentedCount! : summary.staleCount
  const label = hasAdmin ? 'Admin consented' : 'Stale apps'
  const hint = hasAdmin
    ? 'Granted by an admin on behalf of the tenant'
    : 'No sign-in in the last 90 days'

  return <KpiCard label={label} value={value} hint={hint} accent="#4F86A8" />
}

/* -------------------------------------------------------------------------- */
/* Phase B §8.2 — Top-three triptych                                          */
/* -------------------------------------------------------------------------- */

/** Severity-tinted dropcap colors (matches PriorityQueue's SEV_COLOR). */
const TIER_DROPCAP_COLOR: Record<OAuthApp['overallRiskTier'], string> = {
  Critical: '#C84B4B',
  High: '#C9AD80',
  Medium: '#4F86A8',
  Low: '#4A9B6E',
}

const TIER_PILL_NUMBER: Record<OAuthApp['overallRiskTier'], string> = {
  Critical: 'Tier-1',
  High: 'Tier-1',
  Medium: 'Tier-2',
  Low: 'Tier-3',
}

const ACTION_LABEL: Record<NonNullable<OAuthApp['aiRecommendationAction']>, string> = {
  REVOKE: 'Revoke',
  REDUCE: 'Reduce',
  REVIEW: 'Review',
  ACCEPT: 'Approve',
}

function TopThreeTriptych({
  apps,
  details,
  onAppClick,
}: {
  apps: OAuthApp[]
  details: (OAuthAppDetail | undefined)[]
  onAppClick: (app: OAuthApp) => void
}) {
  if (apps.length === 0) {
    return (
      <section className="mt-10">
        <SectionEyebrow text="Section · 01 · Top three" />
        <SectionHeadline level="page">
          Three apps <em>worth a closer look.</em>
        </SectionHeadline>
        <div className="mt-6 rounded-[4px] border border-dashed border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] px-6 py-8 text-center text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
          No tier-one apps demand attention right now.
        </div>
      </section>
    )
  }

  return (
    <section className="mt-10">
      <SectionEyebrow text="Section · 01 · Top three" />
      <SectionHeadline level="page">
        Three apps <em>worth a closer look.</em>
      </SectionHeadline>
      <p className="mt-2 max-w-2xl text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
        Ranked by risk tier, then AI score, then privileged scope count.
      </p>

      <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {apps.map((app, idx) => (
          <TriptychCard
            key={app.id}
            app={app}
            rank={idx + 1}
            detail={details[idx]}
            onClick={() => onAppClick(app)}
          />
        ))}
      </div>
    </section>
  )
}

function TriptychCard({
  app,
  rank,
  detail,
  onClick,
}: {
  app: OAuthApp
  rank: number
  detail: OAuthAppDetail | undefined
  onClick: () => void
}) {
  const dropcapColor = TIER_DROPCAP_COLOR[app.overallRiskTier] ?? '#4F86A8'
  const tierLabel = `${app.overallRiskTier} · ${TIER_PILL_NUMBER[app.overallRiskTier]} · App`
  const ctaAction = app.aiRecommendationAction ?? 'REVIEW'
  const ctaLabel = ACTION_LABEL[ctaAction]
  const why = whyItMatters(app)

  // Pull top-3 named scopes from the detail fetch. Sort by riskTier desc.
  const topScopes = useMemo<OAuthAppPermission[]>(() => {
    if (!detail) return []
    const tierRank: Record<OAuthAppPermission['riskTier'], number> = {
      Critical: 0,
      High: 1,
      Medium: 2,
      Low: 3,
    }
    const all = [...detail.applicationPermissions, ...detail.delegatedPermissions]
    return [...all]
      .sort(
        (a, b) =>
          (tierRank[a.riskTier] ?? 99) - (tierRank[b.riskTier] ?? 99),
      )
      .slice(0, 3)
  }, [detail])

  return (
    <article className="flex flex-col rounded-[4px] border border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)] p-5">
      <header className="flex items-start gap-4">
        <span
          aria-hidden
          className="font-display flex-none select-none italic font-extrabold tabular-nums leading-none tracking-[-0.02em]"
          style={{ fontSize: '40px', color: dropcapColor, minWidth: '46px' }}
        >
          {String(rank).padStart(2, '0')}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[16px] font-semibold leading-snug text-[color:var(--color-navy,#212938)]">
            {app.displayName}
          </div>
          <div className="mt-1 flex items-center gap-2 text-[12px] text-[color:var(--color-mute,#6B7280)]">
            <span className="truncate">
              {app.publisherDomain ?? app.publisherName ?? 'Unknown publisher'}
            </span>
            {app.isFirstParty ? (
              <span className="inline-flex items-center gap-1 rounded-[2px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-cream,#FBF8F2)] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-[color:var(--color-mute,#6B7280)]">
                <CheckBadgeIcon className="h-3 w-3" />
                First-party
              </span>
            ) : app.isVerifiedPublisher ? (
              <span
                className="inline-flex items-center gap-1 rounded-[2px] border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em]"
                style={{
                  borderColor: '#A3CDB0',
                  background: 'rgba(118,214,157,0.15)',
                  color: '#3F8259',
                }}
              >
                <CheckBadgeIcon className="h-3 w-3" />
                Verified
              </span>
            ) : (
              <span
                className="inline-flex items-center gap-1 rounded-[2px] border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em]"
                style={{
                  borderColor: '#E1C99A',
                  background: 'rgba(235,212,180,0.40)',
                  color: '#A88556',
                }}
              >
                Unverified
              </span>
            )}
          </div>
        </div>
      </header>

      <div
        className="mt-4 inline-flex w-fit items-center gap-1.5 rounded-[2px] border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]"
        style={{
          borderColor: dropcapColor,
          color: dropcapColor,
          background: `${dropcapColor}14`,
        }}
      >
        <span
          className="inline-block h-1.5 w-1.5 rounded-full"
          style={{ background: dropcapColor }}
          aria-hidden
        />
        {tierLabel}
      </div>

      <p className="mt-4 text-[13px] leading-relaxed text-[color:var(--color-navy,#212938)]/85">
        {renderInlineEmphasis(why)}
      </p>

      <div className="mt-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)]">
          Top scopes
        </div>
        {detail ? (
          topScopes.length === 0 ? (
            <p className="mt-1 text-[12px] italic text-[color:var(--color-faint,#9CA3AF)]">
              No granted permissions on record.
            </p>
          ) : (
            <ul className="mt-1.5 flex flex-wrap gap-1.5">
              {topScopes.map((p) => (
                <li
                  key={p.id}
                  className="rounded-[2px] border border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-cream,#FBF8F2)] px-2 py-0.5 font-mono text-[11px] text-[color:var(--color-navy,#212938)]"
                  title={p.permissionDescription ?? p.permissionName}
                >
                  {p.permissionName}
                </li>
              ))}
            </ul>
          )
        ) : (
          <p className="mt-1 text-[12px] italic text-[color:var(--color-faint,#9CA3AF)]">
            Loading scopes…
          </p>
        )}
      </div>

      <div className="mt-auto pt-5">
        <button
          onClick={onClick}
          className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-[color:var(--color-secondaryBlue-300,#4F86A8)] hover:opacity-80"
        >
          {ctaLabel}
          <span aria-hidden>→</span>
        </button>
      </div>
    </article>
  )
}

/** Render inline `**bold**` emphasis without any em-dash injection. */
function renderInlineEmphasis(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-semibold text-[color:var(--color-navy,#212938)]">
          {part.slice(2, -2)}
        </strong>
      )
    }
    return <span key={i}>{part}</span>
  })
}

/* -------------------------------------------------------------------------- */
/* Phase C §8.3 + §8.4 — Permission heatmap + publisher-trust donut           */
/* -------------------------------------------------------------------------- */

const HEATMAP_OPS = ['Read', 'ReadWrite', 'Manage', 'SendAct'] as const
const HEATMAP_SCOPES = ['User', 'Group', 'Tenant', 'ServicePrincipal'] as const

const HEATMAP_OP_LABELS: Record<(typeof HEATMAP_OPS)[number], string> = {
  Read: 'Read',
  ReadWrite: 'Read+Write',
  Manage: 'Manage',
  SendAct: 'Send / Act',
}

const HEATMAP_SCOPE_LABELS: Record<(typeof HEATMAP_SCOPES)[number], string> = {
  User: 'User',
  Group: 'Group',
  Tenant: 'Tenant-wide',
  ServicePrincipal: 'Service Principal',
}

function intensityForHeatmap(
  count: number,
  opIsManage: boolean,
  scopeIsTenant: boolean,
): Intensity {
  if (count === 0) return 'l0'
  if (count <= 2) return 'l1'
  if (count <= 5) return 'l2'
  if (count <= 10) return opIsManage || scopeIsTenant ? 'l4' : 'l3'
  return 'l4'
}

function PermissionHeatmapSection({
  heatmap,
  isLoading,
  summary,
}: {
  heatmap: PermissionHeatmap | undefined
  isLoading: boolean
  summary: OAuthAppSummary | undefined
}) {
  const heatmapRows = useMemo<HeatmapRow[]>(() => {
    if (!heatmap?.grid) return []
    return HEATMAP_OPS.map((op) => {
      const opIsManage = op === 'Manage' || op === 'SendAct'
      const cells = HEATMAP_SCOPES.map((scope) => {
        const count = heatmap.grid?.[op]?.[scope] ?? 0
        const scopeIsTenant = scope === 'Tenant'
        const intensity = intensityForHeatmap(count, opIsManage, scopeIsTenant)
        return {
          value: count,
          intensity,
          crit: opIsManage && scopeIsTenant && count > 0,
        }
      })
      return {
        label: HEATMAP_OP_LABELS[op],
        cells,
      }
    })
  }, [heatmap])

  return (
    <section className="mt-10">
      <SectionEyebrow text="Section · 02 · Permission heatmap" />
      <SectionHeadline level="page">
        How permissions <em>are spent.</em>
      </SectionHeadline>
      <p className="mt-2 max-w-2xl text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
        Distinct apps holding at least one permission in each operation × scope cell.
        Manage and tenant-wide cells get the warmest treatment.
      </p>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
        <div>
          {isLoading ? (
            <div className="rounded-[4px] border border-dashed border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] px-6 py-8 text-center text-[13px] italic text-[color:var(--color-mute,#6B7280)]">
              Reading the permission grid…
            </div>
          ) : heatmapRows.length === 0 ? (
            <div className="rounded-[4px] border border-dashed border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] px-6 py-8 text-center text-[13px] italic text-[color:var(--color-mute,#6B7280)]">
              No classifiable permissions found yet.
            </div>
          ) : (
            <EditorialHeatmap
              rows={heatmapRows}
              columns={HEATMAP_SCOPES.map((s) => HEATMAP_SCOPE_LABELS[s])}
              labelWidth={140}
            />
          )}
        </div>

        <div>
          <PublisherTrustDonut summary={summary} />
        </div>
      </div>
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* Phase C §8.4 — Publisher trust donut                                        */
/* -------------------------------------------------------------------------- */

function PublisherTrustDonut({
  summary,
}: {
  summary: OAuthAppSummary | undefined
}) {
  const haveData =
    summary &&
    typeof summary.unverifiedPublisherCount === 'number' &&
    typeof summary.firstPartyCount === 'number'

  if (!summary || !haveData) {
    return (
      <div className="rounded-[4px] border border-dashed border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] px-5 py-8 text-center text-[12px] italic text-[color:var(--color-mute,#6B7280)]">
        Publisher composition not available yet.
      </div>
    )
  }

  // Backend `totalApps` already excludes first-party Microsoft apps, so the
  // donut total is the sum of non-first-party + first-party — and `verified`
  // is just the non-first-party remainder once unverified is taken out.
  const nonFirstParty = summary.totalApps ?? 0
  const unverified = summary.unverifiedPublisherCount ?? 0
  const firstParty = summary.firstPartyCount ?? 0
  const verified = Math.max(0, nonFirstParty - unverified)
  const chartTotal = nonFirstParty + firstParty

  if (chartTotal === 0) {
    return (
      <div className="rounded-[4px] border border-dashed border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] px-5 py-8 text-center text-[12px] italic text-[color:var(--color-mute,#6B7280)]">
        No apps to chart yet.
      </div>
    )
  }

  // Donut geometry.
  const r = 56
  const stroke = 18
  const c = 2 * Math.PI * r
  const segments = [
    { label: 'Verified', value: verified, color: '#4A9B6E' },
    { label: 'Unverified', value: unverified, color: '#C9AD80' },
    { label: 'First-party', value: firstParty, color: '#4F86A8' },
  ]
  let offset = 0

  return (
    <div className="rounded-[4px] border border-[color:var(--color-line,#E6E8EC)] bg-[color:var(--color-paper,#fff)] p-5">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)]">
        Publisher trust
      </div>

      <div className="mt-3 flex items-center gap-5">
        <svg
          width={140}
          height={140}
          viewBox="0 0 140 140"
          className="flex-none"
          aria-label={`${chartTotal} apps split across publisher trust segments`}
        >
          <circle
            cx={70}
            cy={70}
            r={r}
            stroke="#F0EDE5"
            strokeWidth={stroke}
            fill="none"
          />
          {segments.map((seg) => {
            if (seg.value === 0) return null
            const segLen = (seg.value / chartTotal) * c
            const dasharray = `${segLen} ${c - segLen}`
            const dashoffset = -offset
            const node = (
              <circle
                key={seg.label}
                cx={70}
                cy={70}
                r={r}
                stroke={seg.color}
                strokeWidth={stroke}
                fill="none"
                strokeDasharray={dasharray}
                strokeDashoffset={dashoffset}
                transform="rotate(-90 70 70)"
                strokeLinecap="butt"
              />
            )
            offset += segLen
            return node
          })}
          <text
            x={70}
            y={68}
            textAnchor="middle"
            className="font-display fill-[color:var(--color-navy,#212938)]"
            style={{ fontSize: '20px', fontWeight: 700 }}
          >
            {chartTotal.toLocaleString()}
          </text>
          <text
            x={70}
            y={86}
            textAnchor="middle"
            className="fill-[color:var(--color-mute,#6B7280)]"
            style={{ fontSize: '10px', letterSpacing: '0.14em', fontWeight: 700 }}
          >
            APPS
          </text>
        </svg>

        <ul className="flex-1 space-y-1.5 text-[12px]">
          {segments.map((seg) => (
            <li key={seg.label} className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2 text-[color:var(--color-navy,#212938)]">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-[2px]"
                  style={{ background: seg.color }}
                  aria-hidden
                />
                {seg.label}
              </span>
              <span className="font-display tabular-nums font-semibold text-[color:var(--color-navy,#212938)]">
                {seg.value.toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Phase C §8.5 — Recent consents                                              */
/* -------------------------------------------------------------------------- */

function RecentConsentsSection({
  rows,
  isLoading,
}: {
  rows: ConsentRecordRow[] | undefined
  isLoading: boolean
}) {
  return (
    <section className="mt-10">
      <SectionEyebrow text="Section · 03 · Consent activity" />
      <SectionHeadline level="page">
        Who is <em>granting what.</em>
      </SectionHeadline>
      <p className="mt-2 max-w-2xl text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
        Most recent consent grants captured during the last sync.
      </p>

      <div className="mt-6">
        <ConsentFeed rows={rows} isLoading={isLoading} />
      </div>
    </section>
  )
}
